import SparkMD5 from 'spark-md5';

// Keep in sync with the multipart CHUNK_SIZE used by the uploaders.
const DEFAULT_MD5_CHUNK_SIZE = 150 * 1024 * 1024; // 150 MiB

/**
 * Streaming MD5 of the whole file, computed slice-by-slice.
 *
 * Reads the file in chunks of `chunkSize` and feeds each chunk into a single
 * incremental SparkMD5 hasher. The whole file is never held in memory at once,
 * which is required for multi-gigabyte multipart uploads (avoids OOM / tab hang).
 *
 * @param file file/blob to hash
 * @param chunkSize slice size in bytes (defaults to the multipart chunk size)
 * @returns hex MD5 digest of the entire file
 */
export async function computeFileMd5(
  file: Blob,
  chunkSize: number = DEFAULT_MD5_CHUNK_SIZE,
): Promise<string> {
  const spark = new SparkMD5.ArrayBuffer();
  // For an empty file we still run one (empty) append so we return a valid digest.
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));

  for (let index = 0; index < totalChunks; index++) {
    const start = index * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const arrayBuffer = await file.slice(start, end).arrayBuffer();
    spark.append(arrayBuffer);
  }

  return spark.end();
}
